import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { Business } from '../types';

interface User extends FirebaseUser {
  businessId?: string;
}

interface AuthContextType {
  user: User | null;
  business: Business | null;
  loading: boolean;
  isStripeReturn: boolean;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Check if this is a return from Stripe checkout
 * When returning from Stripe, Firebase auth takes time to initialize
 * We need to show a loading state to prevent accidental redirects to login
 */
const checkIsStripeReturn = (): boolean => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.has('session_id') || params.get('success') === 'true';
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [isStripeReturn] = useState(() => checkIsStripeReturn());

  // Track if we've received the first auth state to prevent premature redirects
  const hasReceivedAuthState = useRef(false);
  // Extended timeout for Stripe returns to allow Firebase to fully initialize
  const stripeReturnTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Minimum loading time to prevent auth flicker (allows Firebase to check cache)
  const minLoadingTimeRef = useRef<boolean>(false);

  useEffect(() => {
    // For Stripe returns, give Firebase extra time to restore the session
    // This prevents the "flash of logged out state" issue
    if (isStripeReturn && !hasReceivedAuthState.current) {
      stripeReturnTimeoutRef.current = setTimeout(() => {
        // If we still haven't received auth state after 5 seconds, stop waiting
        if (!hasReceivedAuthState.current) {
          console.warn('Auth state timeout on Stripe return - proceeding anyway');
          setLoading(false);
        }
      }, 5000);
    }

    // Minimum loading time to prevent auth flicker on initial load
    // This gives Firebase time to check cached credentials
    const minLoadingTimer = setTimeout(() => {
      minLoadingTimeRef.current = true;
    }, 150);

    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      hasReceivedAuthState.current = true;

      // Clear the Stripe return timeout since we got auth state
      if (stripeReturnTimeoutRef.current) {
        clearTimeout(stripeReturnTimeoutRef.current);
        stripeReturnTimeoutRef.current = null;
      }

      if (firebaseUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          const userData = userDoc.data();

          const extendedUser = {
            ...firebaseUser,
            businessId: userData?.businessId,
          } as User;

          setUser(extendedUser);

          if (userData?.businessId) {
            const businessDoc = await getDoc(doc(db, 'businesses', userData.businessId));
            if (businessDoc.exists()) {
              setBusiness({ id: businessDoc.id, ...businessDoc.data() } as Business);
            }
          }
        } catch (err) {
          console.error('Error loading user data:', err);
          // Still set the basic user even if Firestore fails
          setUser(firebaseUser as User);
        }
      } else {
        setUser(null);
        setBusiness(null);
      }

      // Only set loading to false after minimum time has passed
      // This prevents auth flicker when Firebase checks cached credentials
      const setLoadingFalse = () => setLoading(false);
      if (minLoadingTimeRef.current) {
        setLoadingFalse();
      } else {
        // Wait for minimum loading time to complete
        const waitTimer = setTimeout(setLoadingFalse, 150);
        return () => clearTimeout(waitTimer);
      }
    });

    return () => {
      unsubscribe();
      clearTimeout(minLoadingTimer);
      if (stripeReturnTimeoutRef.current) {
        clearTimeout(stripeReturnTimeoutRef.current);
      }
    };
  }, [isStripeReturn]);

  const logout = async () => {
    await auth.signOut();
  };

  // Function to manually refresh user data after signup/profile changes
  const refreshUser = useCallback(async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return;

    setLoading(true);
    try {
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      const userData = userDoc.data();

      const extendedUser = {
        ...firebaseUser,
        businessId: userData?.businessId,
      } as User;

      setUser(extendedUser);

      if (userData?.businessId) {
        const businessDoc = await getDoc(doc(db, 'businesses', userData.businessId));
        if (businessDoc.exists()) {
          setBusiness({ id: businessDoc.id, ...businessDoc.data() } as Business);
        }
      }
    } catch (err) {
      console.error('Error refreshing user data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, business, loading, isStripeReturn, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
